import { ReactElement, useEffect, useMemo } from 'react';
import { Controller } from 'react-hook-form';

import { useHirePlanForm } from '../../../../../contexts/HirePlanFormProvider';
import { fetchPlan } from '../../../../../requests/requests';
import { useBoostedPlanDataStore } from '../../../../../stores/useBoostedPlanDataStore';
import { useNordesteStore } from '../../../../../stores/useNordesteStore';
import { usePaymentStore } from '../../../../../stores/usePaymentStore';
import {
  IndividualWithShortageNorthEastIds,
  IndividualWithShortageNorthEastTokens,
  IndividualWithShortageTokens,
} from '../../../../../utils/Enums/Tokens';
import { paymentOptions } from '../data/paymentOptions';
import { PaymentOptionDescription } from './PaymentOptionDescription';
import { PaymentBilletForm } from './PaymentOptionsForms/BilletForm';
import { PaymentConsignadoForm } from './PaymentOptionsForms/ConsignadoForm';
import { PaymentCreditCardForm } from './PaymentOptionsForms/CreditCardForm';
import { PaymentDebitForm } from './PaymentOptionsForms/DebitForm';
import { PaymentSelectorItem } from './PaymentSelectorItem';

type ListaFormaPagamento = {
  gmeioPagto?: number | string;
};

type FormaPagamento = {
  cd_gmeiopagto?: number | string;
  meioPagamentoIndividual?: {
    cd_gmeiopagto?: number | string;
  };
};

function getPaymentGroups(planData: any): number[] {
  const grupos = new Set<number>();
  const listaFormaPagamentos = planData?.listaFormaPagamentos;

  if (
    listaFormaPagamentos &&
    typeof listaFormaPagamentos === 'object' &&
    !Array.isArray(listaFormaPagamentos)
  ) {
    Object.values(listaFormaPagamentos).forEach(
      (paymentOption: ListaFormaPagamento) => {
        const grupo = Number(paymentOption?.gmeioPagto);

        if (grupo) {
          grupos.add(grupo);
        }
      },
    );
  }

  if (grupos.size === 0 && Array.isArray(planData?.formasPagamento)) {
    planData.formasPagamento.forEach((paymentOption: FormaPagamento) => {
      const grupo = Number(
        paymentOption?.meioPagamentoIndividual?.cd_gmeiopagto ||
          paymentOption?.cd_gmeiopagto,
      );

      if (grupo) {
        grupos.add(grupo);
      }
    });
  }

  return Array.from(grupos);
}

export function PaymentSelector(): ReactElement {
  const initialPlanData = useBoostedPlanDataStore(state => state.data);
  const setPaymentId = usePaymentStore(state => state.setId);
  const paymentId = usePaymentStore(state => state.id);
  const setBoostPlanData = useBoostedPlanDataStore(
    state => state.setBoostPlanData,
  );
  const NE = useNordesteStore(x => x.data);

  async function handlepaymentNorthEastChange(type: number) {
    const vendedorId = initialPlanData.vendedorId;
    switch (initialPlanData.token) {
      case IndividualWithShortageNorthEastTokens.CLINICO:
      case IndividualWithShortageNorthEastTokens.CLINICO_AN:
      case IndividualWithShortageTokens.CLINICO:
      case IndividualWithShortageTokens.CLINICONA: {
        const response = await fetchPlan(
          type == 1
            ? IndividualWithShortageNorthEastIds.CLINICO_AN
            : IndividualWithShortageNorthEastIds.CLINICO,
          vendedorId,
        );
        setBoostPlanData(response);
        break;
      }
      case IndividualWithShortageNorthEastTokens.ORTO:
      case IndividualWithShortageNorthEastTokens.ORTO_AN:
      case IndividualWithShortageTokens.ORTO: {
        const response = await fetchPlan(
          type == 1
            ? IndividualWithShortageNorthEastIds.ORTO_AN
            : IndividualWithShortageNorthEastIds.ORTO,
          vendedorId,
        );
        setBoostPlanData(response);
        break;
      }
      case IndividualWithShortageNorthEastTokens.DOC:
      case IndividualWithShortageNorthEastTokens.DOC_AN:
      case IndividualWithShortageTokens.DOC: {
        const response = await fetchPlan(
          type == 1
            ? IndividualWithShortageNorthEastIds.DOC_AN
            : IndividualWithShortageNorthEastIds.DOC,
          vendedorId,
        );
        setBoostPlanData(response);
        break;
      }
      case IndividualWithShortageNorthEastTokens.PROTESE:
      case IndividualWithShortageNorthEastTokens.PROTESE_AN:
      case IndividualWithShortageTokens.PROTESE: {
        const response = await fetchPlan(
          type == 1
            ? IndividualWithShortageNorthEastIds.PROTESE_AN
            : IndividualWithShortageNorthEastIds.PROTESE,
          vendedorId,
        );
        setBoostPlanData(response);
        break;
      }
      case IndividualWithShortageNorthEastTokens.KIDS:
      case IndividualWithShortageNorthEastTokens.KIDS_AN:
      case IndividualWithShortageTokens.KIDS: {
        const response = await fetchPlan(
          type == 1
            ? IndividualWithShortageNorthEastIds.KIDS_AN
            : IndividualWithShortageNorthEastIds.KIDS,
          vendedorId,
        );
        setBoostPlanData(response);
        break;
      }
    }
  }

  const grupoPagamento = useMemo(
    () => getPaymentGroups(initialPlanData),
    [initialPlanData],
  );
  const activePaymentId = grupoPagamento.includes(paymentId) ? paymentId : 0;

  useEffect(() => {
    if (grupoPagamento.length > 0 && !grupoPagamento.includes(paymentId)) {
      setPaymentId(grupoPagamento[0]);
    }
  }, [grupoPagamento, paymentId, setPaymentId]);

  const setTipoPagamento = async (tipo: number) => {
    if (NE) {
      await handlepaymentNorthEastChange(tipo);
    }
    setPaymentId(tipo);
  };

  return (
    <>
      <div className="flex max-[1000px]:flex-col max-[1000px]:items-center w-full min-[748px]:mt-20">
        <nav
          className="min-[748px] w-full"
          aria-label="Opções de métodos de pagamento"
        >
          <ul className="flex flex-row gap-[14px] ">
            {paymentOptions.map(option => {
              for (const grupo of grupoPagamento) {
                if (grupo == option.id) {
                  return (
                    <PaymentSelectorItem
                      key={option.id}
                      label={option.label}
                      active={option.id === activePaymentId}
                      onClick={() => setTipoPagamento(option.id)}
                      Icon={option.Icon}
                      title={option.label}
                    />
                  );
                }
              }
            })}
          </ul>
        </nav>
      </div>
      {activePaymentId == 1 ? (
        <>
          <PaymentOptionDescription paymentId={activePaymentId} />
          <PaymentCreditCardForm />
        </>
      ) : activePaymentId == 3 ? (
        <>
          <PaymentOptionDescription paymentId={activePaymentId} />
          <PaymentBilletForm />
        </>
      ) : activePaymentId == 2 ? (
        <>
          <PaymentOptionDescription paymentId={activePaymentId} />
          <PaymentDebitForm />
        </>
      ) : (
        (activePaymentId == 4 || activePaymentId == 5) && (
          <>
            <PaymentOptionDescription paymentId={activePaymentId} />
            <PaymentConsignadoForm />
          </>
        )
      )}
    </>
  );
}
